/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';

import type { CompactResearchStep } from '../../compact-research-steps/CompactResearchSteps';
import compactResearchMessages from '../../compact-research-steps/messages';
import {
	RotatingThinkingIndicator,
	type RotatingThinkingIndicatorHandle,
} from '../thinking-indicator/RotatingThinkingIndicator';
import { RovoThinkingIndicator } from '../thinking-indicator/ThinkingIndicator';

import { coreMessages, RARE_MESSAGES_PER_CYCLE, rareMessages } from './messages';

/**
 * Fisher-Yates shuffle — returns a new shuffled copy of the array.
 */
function shuffle<T>(array: T[]): T[] {
	const result = [...array];
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
}

type ResearchRotatingThinkingIndicatorProps = {
	steps: CompactResearchStep[];
	researchCompleted: boolean;
	thinkDeeperTriggered: boolean;
	isAnimated?: boolean;
	icon?: boolean | React.ReactNode;
	animation?: boolean;
	disableMorphingAnimation?: boolean;
	showEllipsis?: boolean;
	testId?: string;
};

/**
 * Domain-specific wrapper that composes `RotatingThinkingIndicator` with
 * research-aware reset and disable logic.
 *
 * - **Resets** the rotation whenever the research steps change (new step
 *   arrives or a step completes), so rotation only kicks in after a period
 *   of inactivity.
 * - **Disables** rotation (falls back to static `RovoThinkingIndicator`)
 *   when `researchCompleted` is `true`.
 * - Owns the i18n-formatted rotating text pool (core + weighted rare messages).
 */
export const ResearchRotatingThinkingIndicator = ({
	steps,
	researchCompleted,
	thinkDeeperTriggered,
	isAnimated,
	icon,
	animation,
	disableMorphingAnimation,
	showEllipsis,
	testId,
}: ResearchRotatingThinkingIndicatorProps): JSX.Element => {
	const { formatMessage } = useIntl();

	// ── Static title (shown during initial delay / when rotation is off) ──
	const staticTitle = formatMessage(
		thinkDeeperTriggered
			? compactResearchMessages.thinkDeeperThinking
			: compactResearchMessages.rovoIsThinking,
	);

	// ── Build the weighted rotating text pool ─────────────────────────────
	// Memoised so the array identity is stable across renders.
	const rotatingTexts = useMemo(() => {
		const core = Object.values(coreMessages).map((msg) => formatMessage(msg));
		const rare = Object.values(rareMessages).map((msg) => formatMessage(msg));
		// Pick a random subset of rare messages to mix in
		const selectedRare = shuffle(rare).slice(0, RARE_MESSAGES_PER_CYCLE);
		return [...core, ...selectedRare];
	}, [formatMessage]);

	const rotatingRef = useRef<RotatingThinkingIndicatorHandle>(null);

	// ── Reset rotation whenever the step landscape changes ────────────────
	// A fingerprint derived from each step's full state (id, completion,
	// streaming status, and actual content) ensures we reset on any
	// meaningful change — including content streaming into a step.
	const stepFingerprint = useMemo(() => {
		return steps
			.map(
				(s) =>
					`${s.stepId}:${s.completed ?? false}:${s.isStreamed ?? false}:${s.separatedContent.join(',')}`,
			)
			.join('|');
	}, [steps]);

	useEffect(() => {
		rotatingRef.current?.resetRotation();
	}, [stepFingerprint]);

	// ── Suppress rotation when research is complete or any step is still in progress ──
	// Rotation only kicks in when all visible steps are completed but we're
	// waiting for the next step or the final answer.
	const hasInProgressSteps = steps.some((s) => !s.completed);
	if (researchCompleted || hasInProgressSteps) {
		return (
			<RovoThinkingIndicator
				title={staticTitle}
				isAnimated={isAnimated}
				icon={icon}
				animation={animation}
				disableMorphingAnimation={disableMorphingAnimation}
				showEllipsis={showEllipsis}
				testId={testId}
			/>
		);
	}

	return (
		<RotatingThinkingIndicator
			ref={rotatingRef}
			title={staticTitle}
			rotatingTitleConfig={{
				texts: rotatingTexts,
			}}
			isAnimated={isAnimated}
			icon={icon}
			animation={animation}
			disableMorphingAnimation={disableMorphingAnimation}
			showEllipsis={showEllipsis}
			testId={testId}
		/>
	);
};
